import { useEffect, useState } from 'react';

import './App.css';
import 'rsuite/dist/rsuite.min.css';
import './index.css';
import 'video-react/dist/video-react.css'; // import css
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { MessengerProvider } from './context/messengerContext';
import { LanguageProvider } from './context/localeContext';

// Routes
import CreateInvoice from './routes/createInvoice/view';
import CreateTemplate from './routes/createTemplate/view';
import UpdateTemplate from './routes/updateTemplate/view';
import CalculateInvoice from './routes/calculateInvoice/view';
import AuthPanel from './routes/auth/0_authPanel';
import AuthContact from './routes/auth/1_auth_contact';
import CreateClaim from './routes/claim/createClaim';
import TrackInvoice from './routes/trackInvoice/trackInvoice';
import TrackingByNumber from './routes/packageTracking/packageTracking';
import TrackingByBot from './routes/packageTracking/packageTrackingBot';
import FeedbackPanel from './routes/botRaitingPanel/botRaitingPanel';
import EnterChat from './routes/enterChat/enterChat';
import Instructions from './routes/instructions/view';
import InstructionsList from './routes/instructions/instructionsList';

// Components
import FirstTimeModal from './components/firstTimeModal/firstTimeModal';
import { useFirstTimeModal } from './hooks/useFirstTimeModal';
import TestModal from './components/firstTimeModal/TestModal';

// Utils
import './utils/firstTimeModalUtils'; // Import for development utilities

import { Logtail } from '@logtail/browser';
import { get_server_version } from './api/debug';
import NetworkMonitor from './components/NetworkMonitor';
import { setupFetchInterceptor } from './api/axiosInterceptors';
import TemplatesList from './routes/templates/templatesList';
import ContractsList from './routes/contracts/contractsList';
import packageJson from '../package.json';
import EmployeesList from './routes/employees/employeesList';
import HomePage from './routes/home/HomePage';
import IndexPage from './routes/index/IndexPage';
import NavMenu from './components/navMenu/NavMenu';
import DebugOverlay from './components/debugOverlay/DebugOverlay';
import EmployeeInvite from './routes/employees/employeeInvite';
import NotificationsList from './routes/notifications/NotificationsList';
import NotificationDetail from './routes/notifications/NotificationDetail';
import ProfilePage from './routes/profile/ProfilePage';
import ProfileUpdatePage from './routes/profile/ProfileUpdatePage';
import InstallPage from './routes/install/InstallPage';
import AppBackButton from './components/AppBackButton';
import ErrorBoundary from './components/ErrorBoundary';

const logtail = new Logtail('aWiRA4tki7wdzxFFFkqxqYKj', {
    endpoint: 'https://in.logs.betterstack.com',
});

async function fetchEnd(logData, url, method, body) {
    let b = body;

    try {
        b = JSON.parse(body);
    } catch {
        console.log(body);
    }

    //logtail.info(`Request: ${method} ${url}`, {...logData, body: b});
}

function fetchEndError() {}

function AppContent() {
    const [version, setVersion] = useState('0.0.0');
    const theme = 'light';
    const { modalState, closeModal } = useFirstTimeModal();

    useEffect(() => {
        const updateWidth = (element) => {
            if (element) {
                const minWidth = window.getComputedStyle(element).minWidth;
                if (minWidth !== '0px') {
                    element.style.width = minWidth;
                }
            }
        };

        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (
                        node instanceof HTMLElement &&
                        node.classList.contains('rs-picker-popup')
                    ) {
                        updateWidth(node);
                    }
                });
            });
        });

        observer.observe(document.body, { childList: true, subtree: true });

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        // Setup fetch interceptor for API performance monitoring
        const restoreFetch = setupFetchInterceptor();

        // Keep the existing logging functionality
        const originalFetch = window.fetch;
        window.fetch = function (...args) {
            let url;
            let method = 'GET';
            let options = {};

            // Extract URL and method from arguments
            if (typeof args[0] === 'string') {
                url = args[0];
                options = args[1] || {};
                method = options.method || 'GET';
            } else if (args[0] instanceof Request) {
                const request = args[0];
                url = request.url;
                method = options.method || request.method;
                options = args[1] || {};
            }

            const logData = {
                url,
                method,
                timestamp: new Date().toISOString(),
            };

            // Use the fetch already wrapped by our interceptor
            return originalFetch
                .apply(this, args)
                .then((response) => {
                    if (url.includes('betterstack')) return response;

                    logData.status = response.status;
                    logData.statusText = response.statusText;
                    try {
                        let r = response.clone();
                        // Check if the response is JSON and if not return none
                        if (
                            !r.headers
                                .get('content-type')
                                ?.includes('application/json')
                        ) {
                            fetchEnd(logData, url, method, options?.body);
                            return response;
                        }

                        r.json().then((data) => {
                            logData.data = data;
                            fetchEnd(logData, url, method, options?.body);
                        });
                    } catch {
                        fetchEnd(logData, url, method, options?.body);
                    }

                    return response;
                })
                .catch((error) => {
                    logData.error = error.message;
                    fetchEndError();
                    throw error;
                });
        };

        return () => {
            restoreFetch(); // Clean up fetch interceptor when component unmounts
        };
    }, []);

    useEffect(() => {
        get_server_version().then((r) => {
            setVersion(r.version);
        });
    }, []);

    return (
        <div className='App'>
            <AppBackButton />
            <NavMenu />
            <DebugOverlay />
            <Routes>
                <Route path='/' element={<IndexPage />} />
                <Route path='/menu' element={<HomePage />} />
                <Route
                    path='/notification_list'
                    element={<NotificationsList />}
                />
                <Route
                    path='/notification/:id'
                    element={<NotificationDetail />}
                />
                <Route path='/invoice' element={<CreateInvoice />} />
                <Route path='/template_list' element={<TemplatesList />} />
                <Route path='/template_create' element={<CreateTemplate />} />
                <Route
                    path='/template_update/:template_id'
                    element={<UpdateTemplate />}
                />
                <Route path='/contract_list' element={<ContractsList />} />
                <Route path='/employee_list' element={<EmployeesList />} />
                <Route path='/employee_invite' element={<EmployeeInvite />} />
                <Route
                    path='/invoice_calculation'
                    element={<CalculateInvoice />}
                />
                <Route path='/auth' element={<AuthPanel />} />
                <Route path='/auth_contact' element={<AuthContact />} />
                <Route path='/pretense' element={<CreateClaim />} />
                <Route path='/tracking_start' element={<TrackInvoice />} />
                <Route
                    path='/tracking_by_number'
                    element={<TrackingByNumber />}
                />
                <Route path='/tracking_from_bot' element={<TrackingByBot />} />
                <Route path='/feedback' element={<FeedbackPanel />} />
                <Route path='/enter_chat' element={<EnterChat />} />
                <Route
                    path='/instructions_list'
                    element={<InstructionsList />}
                />
                <Route path='/instructions' element={<Instructions />} />
                <Route path='/profile' element={<ProfilePage />} />
                <Route path='/profile-update' element={<ProfileUpdatePage />} />
                <Route path='/install' element={<InstallPage />} />
                <Route path='/test-modal' element={<TestModal />} />
            </Routes>

            {/* First Time Modal */}
            <FirstTimeModal
                isOpen={modalState.isOpen}
                onClose={closeModal}
                routeType={modalState.routeType}
                videoUrl={modalState.videoUrl}
                titleKey={modalState.titleKey}
                descriptionKey={modalState.descriptionKey}
            />

            <NetworkMonitor
                checkInterval={5000}
                pingUrl='https://www.google.com/'
                timeout={3000}
                enableVPNDetection={true}
                enableSpeedTest={true}
                speedThreshold={1000}
                showNotifications={true}
                autoReconnect={true}
                maxRetries={3}
                enableApiMonitoring={true}
            />

            <span className='filler'></span>
            <span className='version'>
                <span>
                    Frontend version: {packageJson.version}/ Backend version:{' '}
                    {version}
                </span>
            </span>
        </div>
    );
}

function App() {
    return (
        <ErrorBoundary>
            <BrowserRouter>
                <MessengerProvider>
                    <LanguageProvider theme='light'>
                        <AppContent />
                    </LanguageProvider>
                </MessengerProvider>
            </BrowserRouter>
        </ErrorBoundary>
    );
}

export default App;
